import { Injectable } from '@angular/core';

import { ApiService } from '@frontend/sports/common/api-utils';
import { BetBuilderConfig } from '@frontend/sports/common/client-config-data-access';
import { INativePrice, NativePrice } from '@frontend/sports/odds/feature/native-price';
import { Observable, map } from 'rxjs';

export interface PriceRequest {
    externalFixtureId: number;
    sportId: number;
    bets: string[];
    stake: number;
    saveBetslip?: boolean;
    key: number;
}

export enum ApiKey {
    BetGenius = 1,
    Sportcast = 2,
}

export interface PriceResponse {
    statusCode: Status;
    price: INativePrice | undefined;
    betSlipUid: string;
    isInfinity?: boolean;
    redundantSelections: Selection[];
}

export interface BetBuilderPriceInfo {
    statusCode: Status;
    price: NativePrice | undefined;
    betSlipUid: string;
    isInfinity?: boolean;
    redundantSelections: Selection[];
}

export interface BetBuilderBetFeedback {
    fixtureId: number;
    marketId: number;
}

export interface Selection {
    longId: string;
    information: Information;
}

export enum Status {
    OK = 'OK',
    Fail = 'Fail',
    InternalError = 'InternalError',
    QueueEmpty = 'QueueEmpty',
    InvalidKey = 'InvalidKey',
    InvalidInputParam = 'InvalidInputParam',
    NoFixtures = 'NoFixtures',
    FixtureDisabled = 'FixtureDisabled',
    FixtureExpired = 'FixtureExpired',
    NoTranslations = 'NoTranslations',
    NoTraderVariables = 'NoTraderVariables',
    InvalidMapping = 'InvalidMapping',
    NoGameState = 'NoGameState',
}

export enum Information {
    None = 'None',
    PlayerNotInTraderVariables = 'PlayerNotInTraderVariables',
    IncompatibleMarkets = 'IncompatibleMarkets',
    ImpossiblePlayerCombination = 'ImpossiblePlayerCombination',
    RedundantBetRemoved = 'RedundantBetRemoved',
}

@Injectable({ providedIn: 'root' })
export class BetBuilderPriceService {
    constructor(
        private apiService: ApiService,
        private betBuilderConfig: BetBuilderConfig,
    ) {}

    requestPrice(priceRequest: PriceRequest): Observable<BetBuilderPriceInfo> {
        return this.apiService.post<PriceResponse>('betBuilderPrice/requestPrice', priceRequest).pipe(
            map((priceResponse) => ({
                price: priceResponse.price ? NativePrice.fromNativePrice(priceResponse.price) : undefined,
                betSlipUid: priceResponse.betSlipUid,
                isInfinity: priceResponse.isInfinity,
                redundantSelections: priceResponse.redundantSelections || [],
                statusCode: priceResponse.statusCode,
            })),
        );
    }

    getBetFeedback(betSlipUid: string, betgeniusId: number, key: number): Observable<BetBuilderBetFeedback> {
        return this.apiService.get<BetBuilderBetFeedback>(
            'betBuilderPrice/betFeedback',
            { betSlipUid, betgeniusId, key },
            {
                retryCount: this.betBuilderConfig.betFeedbackRetryCount,
                retryDelay: this.betBuilderConfig.betFeedbackRetryDelay,
                retryOnStatus: [400],
            },
        );
    }
}
